import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';

import { getObjectId } from '@hero/schema';
import { useSendOtpApi, useSignInApi } from '@hero/api-hooks';
import { SDKError } from '@hero/sdk-core';
import { useNav } from '../navigation/useNav';
import { resetSessionCache } from './resetSessionCache';
import {
  clearPhoneResponse,
  getPhoneResponse,
  saveOtpResponse,
  setPhoneResponse,
} from './tokens';

const OTP_LENGTH = 5;

const createOtpSchema = (t: (key: string) => string) =>
  z.object({
    code: z.string().length(OTP_LENGTH, { message: t('validations:otp.length') }),
  });

type OtpFormValues = z.infer<ReturnType<typeof createOtpSchema>>;

export type UseOtpVerifyManagerOptions = {
  /**
   * Where to navigate when the OTP verification succeeds for an
   * EXISTING user. Defaults to `/dashboard`.
   */
  onExistingUser?: () => void;
  /**
   * Where to navigate when the OTP verification succeeds for a NEW
   * user. Defaults to the shared `auth_choose_person` route — the
   * first step of the unified signup wizard.
   */
  onNewUser?: () => void;
};

const RESEND_COUNTDOWN_SECONDS = 60;

/**
 * Unified OTP-verification step. Wraps the unified `auth.signIn`
 * endpoint (which doubles as "verify OTP" for both existing and new
 * users — the server returns `isNewUser` + a temp/real token) and
 * persists the result via the shared `tokens.ts` store.
 *
 * Auto-submits verification when all {@link OTP_LENGTH} digits are
 * entered; clears the code field on failure so the user can retry.
 *
 * Replaces the per-app `useOtpVerify` hooks that used Redux +
 * `verifyOTP(...)` ad-hoc fetch. Identical behavior across
 * customer-dashboard, carrier-dashboard, and admin-panel.
 *
 * See `docs/relations-v2/08-user-identity.md`.
 */
export function useOtpVerifyManager(options: UseOtpVerifyManagerOptions = {}) {
  const { t } = useTranslation(['toast', 'validations']);
  const { navigate } = useNav();
  const queryClient = useQueryClient();
  const { onExistingUser, onNewUser } = options;

  const [countdown, setCountdown] = useState(0);

  const phone = getPhoneResponse().at(-1);
  // After a successful verify, `saveOtpResponse` clears phoneResponse.
  // That recreates `submit` and re-runs the auto-verify effect while
  // the OTP code is still 5 digits — which previously toasted
  // "Missing required data" on the way to choose-person. Gate all
  // post-success re-entry with this ref.
  const verifiedRef = useRef(false);

  const form = useForm<OtpFormValues>({
    resolver: zodResolver(createOtpSchema(t)),
    defaultValues: { code: '' },
  });

  const { mutateAsync: signInAsync, isPending } = useSignInApi();
  const { mutateAsync: sendOtpAsync, isPending: isResend } = useSendOtpApi();

  const submit = useCallback(
    async (values: OtpFormValues) => {
      if (verifiedRef.current) return;
      if (!phone?.phone || !phone?.otpId) {
        toast.error(t('toast:error.missingData'));
        return;
      }
      try {
        const response = await signInAsync({
          phone: phone.phone,
          // Stored as a hex string in localStorage; the API type wants ObjectId.
          otpId: getObjectId(phone.otpId),
          code: values.code,
        });
        verifiedRef.current = true;
        saveOtpResponse({
          isNewUser: response.isNewUser,
          access_token: response.access_token,
          refresh_token: response.refresh_token,
        });
        toast.success(t('toast:success.verification'), { id: 'auth-otp-flow' });

        if (response.isNewUser) {
          if (onNewUser) onNewUser();
          else navigate('auth_choose_person');
        } else {
          await resetSessionCache(queryClient, 'sign-in');
          if (onExistingUser) onExistingUser();
          else window.location.assign('/dashboard');
        }
      } catch (e) {
        if (
          e instanceof SDKError &&
          (e.message === 'auth_user_blocked' ||
            e.code === 'auth_user_blocked' ||
            e.data?.error?.errorId === 'auth_user_blocked')
        ) {
          clearPhoneResponse();
          navigate('account_blocked', { phone: phone.phone });
          return;
        }
        // Clear so the user can re-enter — mirrors mobile `useVerifyOtpManager`
        // and avoids auto-resubmitting the same wrong code.
        form.setValue('code', '');
        const message = e instanceof Error ? e.message : t('toast:error.verification');
        toast.error(message);
      }
    },
    [form, navigate, onExistingUser, onNewUser, phone?.otpId, phone?.phone, queryClient, signInAsync, t],
  );

  const handleResend = useCallback(async () => {
    if (countdown > 0 || !phone?.phone) return;
    try {
      const response = await sendOtpAsync({ phone: phone.phone });
      setPhoneResponse({
        isNewUser: response.isNewUser,
        otpId: response.otpId,
        phone: response.phone,
      });
      toast.success(t('toast:success.otpSent'), { id: 'auth-otp-flow' });
      setCountdown(RESEND_COUNTDOWN_SECONDS);
    } catch (e) {
      const message = e instanceof Error ? e.message : t('toast:error.otpSend');
      toast.error(message);
    }
  }, [countdown, phone?.phone, sendOtpAsync, t]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // Snapshot phone on FIRST render — see useOtpVerify note. The
  // guard must NOT re-fire when `saveOtpResponse` clears the phone
  // response after a successful sign-in, otherwise the intended
  // navigation to the wizard (`auth_choose_person`) is clobbered.
  const hadPhoneOnMount = useRef(!!(phone?.phone && phone?.otpId));

  useEffect(() => {
    if (!hadPhoneOnMount.current) {
      navigate('auth_register');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const code = form.watch('code');

  // Auto-verify once all digits are entered — same UX as mobile
  // `useVerifyOtpManager.onCodeUpdate`.
  useEffect(() => {
    const willSubmit =
      !verifiedRef.current &&
      !isPending &&
      code.length === OTP_LENGTH &&
      !!phone?.phone &&
      !!phone?.otpId;
    if (!willSubmit) return;
    void form.handleSubmit(submit)();
  }, [code, form, isPending, submit, phone?.phone, phone?.otpId]);

  return {
    form,
    isPending,
    isResend,
    countdown,
    onSubmit: form.handleSubmit(submit),
    handleResend,
    /** Clear the cached phone response — useful on "back" navigation. */
    clearPhone: clearPhoneResponse,
    phone: phone?.phone,
  };
}
